// ═══════════════════════════════════════
//  DIGIMINT — COURSE DATA
// ═══════════════════════════════════════

export const BATCH_TARGET = Date.now() + 4 * 86400000 + 11 * 3600000 + 38 * 60000;

export const MODULES = [
  {
    id: 1,
    week: 'Week 1 · 3 sessions',
    title: 'Digital Marketing Foundations',
    topics: [
      'How digital marketing works — the full ecosystem explained',
      'Understanding marketing funnels: Awareness → Interest → Conversion',
      'Overview of paid, organic, and owned media channels',
      'Setting realistic goals and KPIs for campaigns',
    ],
  },
  {
    id: 2,
    week: 'Week 2–3 · 5 sessions',
    title: 'Meta Ads Fundamentals',
    topics: [
      'Facebook Business Manager & Ads Manager setup',
      'Campaign structure: Campaign → Ad Set → Ad',
      'Audience targeting: demographics, interests, lookalikes',
      'Ad formats: image, video, carousel, collection',
      'Budget types: daily vs. lifetime, bidding strategies',
      'Facebook Pixel setup & conversion tracking',
    ],
  },
  {
    id: 3,
    week: 'Week 3–4 · 4 sessions',
    title: 'Advanced Meta Ads & Optimization',
    topics: [
      'Retargeting campaigns and custom audiences',
      'A/B testing ads: what to test & how to read results',
      'Reading ad performance metrics (CTR, CPM, ROAS, CPA)',
      'Scaling a winning ad: horizontal vs. vertical scaling',
      'Troubleshooting underperforming campaigns',
    ],
  },
  {
    id: 4,
    week: 'Week 5–6 · 5 sessions',
    title: 'Google Ads — Search & Display',
    topics: [
      'How Google Ads works: auction, Quality Score, Ad Rank',
      'Keyword research: match types, negative keywords',
      'Writing high-converting search ad copy',
      'Ad extensions: sitelinks, callouts, structured snippets',
      'Display network: targeting, placements, responsive ads',
      'Google Tag Manager & conversion tracking setup',
    ],
  },
  {
    id: 5,
    week: 'Week 6 · 2 sessions',
    title: 'SEO Basics & Landing Page Strategy',
    topics: [
      'On-page SEO fundamentals: titles, meta, headings',
      'What makes a high-converting landing page',
      'CTA placement, trust signals, and form optimization',
      'Connecting your ads to landing pages for better ROAS',
    ],
  },
  {
    id: 6,
    week: 'Week 7 · 3 sessions',
    title: 'Reporting, Analytics & Tools',
    topics: [
      'Google Analytics 4: sessions, events, goals',
      'Building client reports that actually tell a story',
      'Using Canva for ad creative production',
      'Intro to automation and AI tools in marketing',
    ],
  },
  {
    id: 7,
    week: 'Week 8 · 2 sessions',
    title: 'Freelancing, Career & Final Project',
    topics: [
      'How to build a portfolio with zero client experience',
      'Pricing your services: packages & retainers',
      'Job prep: resume, LinkedIn, interview questions',
      'Final project: Run a complete mini-campaign (Meta or Google)',
    ],
  },
];

export const BONUS_MODULES = [
  { icon: '📣', title: 'Influencer Marketing 101', desc: 'Find, brief & measure influencer campaigns' },
  { icon: '📧', title: 'Email Marketing Basics', desc: 'Campaigns, automations, and list building' },
  { icon: '🤖', title: 'AI in Marketing', desc: 'ChatGPT for copy, AI tools for creatives' },
  { icon: '📋', title: 'Real Campaign Case Studies', desc: 'Breakdowns of what worked & what didn\'t' },
];

export const TOOLS = [
  'Meta Ads Manager', 'Facebook Business Suite', 'Google Ads',
  'Google Analytics 4', 'Google Tag Manager', 'Google Search Console',
  'Canva', 'ChatGPT / AI Copywriting', 'Google Keyword Planner', 'Meta Pixel',
];

export const OUTCOMES = [
  { icon: '🎯', title: 'Launch Meta & Instagram Ads', desc: 'Set up, target, budget, and optimize Meta campaigns confidently — from scratch.' },
  { icon: '🔍', title: 'Run Google Search & Display', desc: 'Understand keywords, bidding strategies, ad copy, and quality scores.' },
  { icon: '📊', title: 'Read and Act on Analytics', desc: 'Know which metrics matter, spot underperformance, make data-driven decisions.' },
  { icon: '🖼️', title: 'Create Scroll-Stopping Creatives', desc: 'Use Canva to produce ad visuals that get clicks — no design experience needed.' },
  { icon: '💼', title: 'Land Clients or a Marketing Job', desc: 'Build a portfolio using real projects. Know how to pitch and price your skills.' },
  { icon: '🏢', title: 'Grow Your Own Business', desc: 'Run your own ads, cut agency dependency, and control your growth spending.' },
];

export const TESTIMONIALS = [
  {
    initial: 'P', name: 'Priya Kulkarni', role: 'Jr. Digital Marketer, Pune', rating: 5,
    badge: 'Got Hired ✓', badgeColor: '#10b96a',
    text: '"I had zero marketing background. After this course, I could run a full Facebook campaign and explain the results. Got my first digital marketing job within 6 weeks of finishing."',
  },
  {
    initial: 'R', name: 'Rohit Sharma', role: 'Freelance PPC Specialist', rating: 5,
    badge: 'First Client ✓', badgeColor: '#3b82f6',
    text: '"The Google Ads module alone was worth it. I went from not knowing what a keyword is to landing my first freelance client. The mentor answers every question, no matter how basic."',
  },
  {
    initial: 'A', name: 'Anjali Mehta', role: 'D2C Founder, Pune', rating: 4,
    badge: 'Grew Business ✓', badgeColor: '#f5a623',
    text: '"I run a small clothing brand. Was spending ₹20k/month on an agency with no visibility. After this course I manage my own Meta ads and cut CPA by 40% in the first month."',
  },
];

export const FAQS = [
  {
    q: 'Is this course suitable for complete beginners with no marketing experience?',
    a: 'Absolutely. This course is designed specifically for beginners. We start from zero — no assumptions about prior knowledge. You just need a laptop, internet connection, and the willingness to learn. Many of our best students came in with no marketing background at all.',
  },
  {
    q: 'Does this performance marketing course include an internship?',
    a: 'The course includes a practical final project where you run a real mini-campaign, giving you portfolio-ready work. We also provide career guidance and connect you with relevant opportunities. Internship availability depends on the current batch — ask us during the counselling call for the most current details.',
  },
  {
    q: 'What is the difference between this and free Google Ads or Meta Ads courses online?',
    a: 'Free courses give you isolated information. This course gives you a structured, mentor-guided progression with real campaign practice, live doubt-clearing, a peer community, and post-course support. You also get feedback on your actual work — something no YouTube video can provide.',
  },
  {
    q: 'Is this course available online or only in Pune?',
    a: 'Both. We offer online live sessions (accessible from anywhere in India) and offline classes at our Pune centre. All sessions are recorded. If you\'re looking for Google Ads training online, you can join from anywhere.',
  },
  {
    q: 'How much can I earn after completing this course?',
    a: 'Entry-level digital marketing roles in India typically range from ₹18,000–₹35,000/month. Freelancers handling 3–5 clients often exceed ₹50,000/month. Your income will reflect the skills you build and how you apply them — we won\'t promise specific numbers.',
  },
  {
    q: 'Do I need any budget to run ads during the course?',
    a: 'For most exercises, we use demo accounts and free tools so there\'s no mandatory ad spend. For the optional final project, a small budget (₹500–₹1,000) helps you see real data, but it\'s not required.',
  },
  {
    q: 'Is the Digimint certificate recognized by employers?',
    a: 'Our certificate signals hands-on training completion. We also guide you toward the official Google Ads and Meta Blueprint certifications (both free), which are globally recognized. Most employers care more about your portfolio than certificates — which is why we focus on practical output. The Digimint certificate is a proof of completion backed by real campaign work.',
  },
];

export const COURSE_DETAILS = [
  { icon: '📅', label: 'Duration', value: '8 Weeks' },
  { icon: '🕐', label: 'Time / Week', value: '6–8 Hours' },
  { icon: '🎓', label: 'Level', value: 'Beginner' },
  { icon: '📍', label: 'Mode', value: 'Online + Offline' },
  { icon: '🌐', label: 'Language', value: 'English / Hindi' },
  { icon: '🏆', label: 'Certificate', value: 'On Completion' },
  { icon: '👤', label: 'Eligibility', value: 'Anyone 18+' },
  { icon: '💻', label: 'Prerequisites', value: 'Laptop & Internet' },
];

export const PAIN_POINTS = [
  { emoji: '😩', title: 'No Real Practice', desc: 'You watch videos about ads but never actually run one. Theory alone won\'t get you hired or bring clients.' },
  { emoji: '🤷', title: 'Zero Guidance', desc: 'Recorded courses can\'t answer your specific questions. You\'re left Googling everything yourself.' },
  { emoji: '📃', title: 'Outdated Curriculum', desc: 'Meta and Google update constantly. Most course content is 2+ years old and teaches deprecated flows.' },
  { emoji: '💸', title: 'No ROI Clarity', desc: 'You spend money on a course but don\'t know if it actually translates to a job, client, or real income.' },
];
